import {
  Area,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { formatMoney } from "@/lib/format";
import type { ProfitBucket, ProfitProductRow } from "@/types";

interface TrendChartProps {
  buckets: ProfitBucket[];
}

export function ProfitTrendChart({ buckets }: TrendChartProps) {
  return (
    <div className="h-[300px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={buckets} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="profitRevenueFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="oklch(var(--primary-500))" stopOpacity={0.35} />
              <stop offset="100%" stopColor="oklch(var(--primary-500))" stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="oklch(var(--background-300))" vertical={false} />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 11, fill: "oklch(var(--foreground-500))" }}
            axisLine={false}
            tickLine={false}
            interval="preserveStartEnd"
          />
          <YAxis
            tick={{ fontSize: 11, fill: "oklch(var(--foreground-500))" }}
            axisLine={false}
            tickLine={false}
            width={64}
            tickFormatter={(value: number) => `${Math.round(value / 1000)}k`}
          />
          <Tooltip
            contentStyle={{
              borderRadius: 8,
              border: "1px solid oklch(var(--background-200))",
              background: "oklch(var(--background-50))",
              fontSize: 12,
            }}
            formatter={(value, name) => [
              formatMoney(Number(value)),
              name === "revenue" ? "Ingresos" : name === "profit" ? "Ganancia" : String(name),
            ]}
          />
          <Area
            type="monotone"
            dataKey="revenue"
            stroke="oklch(var(--primary-500))"
            strokeWidth={2}
            fill="url(#profitRevenueFill)"
          />
          <Line
            type="monotone"
            dataKey="profit"
            stroke="oklch(var(--accent-500))"
            strokeWidth={2.5}
            dot={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

interface TopProductsChartProps {
  products: ProfitProductRow[];
}

export function ProfitTopProductsChart({ products }: TopProductsChartProps) {
  const data = products.map((p) => ({
    name: p.name.length > 16 ? `${p.name.slice(0, 15)}…` : p.name,
    profit: p.profit,
    revenue: p.revenue,
  }));

  return (
    <div className="h-[300px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} layout="vertical" margin={{ top: 6, right: 16, left: 8, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="oklch(var(--background-300))" horizontal={false} />
          <XAxis
            type="number"
            tick={{ fontSize: 11, fill: "oklch(var(--foreground-500))" }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(value: number) => `${Math.round(value / 1000)}k`}
          />
          <YAxis
            type="category"
            dataKey="name"
            tick={{ fontSize: 11, fill: "oklch(var(--foreground-700))" }}
            axisLine={false}
            tickLine={false}
            width={110}
          />
          <Tooltip
            cursor={{ fill: "oklch(var(--background-200) / 0.5)" }}
            contentStyle={{
              borderRadius: 8,
              border: "1px solid oklch(var(--background-200))",
              background: "oklch(var(--background-50))",
              fontSize: 12,
            }}
            formatter={(value) => [formatMoney(Number(value)), "Ganancia"]}
          />
          <Bar dataKey="profit" radius={[0, 6, 6, 0]} maxBarSize={26}>
            {data.map((entry) => (
              <Cell key={entry.name} fill="oklch(var(--primary-500))" />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}